import { useCheckUsernameAvailability } from "seedgreen-shared/queries/user";
import { ref, watch, computed, type Ref } from "vue";
import { useDebounceFn } from "@vueuse/core";

export type UsernameAvailabilityStatus =
	| "unchecked"
	| "checking"
	| "available"
	| "taken";

/**
 * Composable for checking username availability in bulk with debouncing.
 * Generic and reusable for any username checking scenario.
 *
 * @param usernames - Computed ref that returns the list of usernames to check
 */
export function useUsernameAvailability(usernames: Ref<string[]>) {
	const { checkUsernameAvailability } = useCheckUsernameAvailability();

	// Map of normalized username -> availability status
	const availabilityMap = ref(new Map<string, UsernameAvailabilityStatus>());

	/**
	 * Normalizes username the same way the backend does (trim + lowercase)
	 */
	const normalizeUsername = (username: string): string => {
		return username.trim().toLowerCase();
	};

	/**
	 * Performs the bulk username availability check
	 */
	const performCheck = async () => {
		// Collect and normalize usernames to check
		const usernamesToCheck = usernames.value
			.filter((u) => u && u.trim().length >= 4) // Basic validation (must be 4+ chars)
			.map(normalizeUsername)
			.filter((u, idx, arr) => arr.indexOf(u) === idx); // Deduplicate

		if (usernamesToCheck.length === 0) {
			return;
		}

		// Mark all as checking
		usernamesToCheck.forEach((username) => {
			availabilityMap.value.set(username, "checking");
		});

		try {
			// Call API
			const result = await checkUsernameAvailability(usernamesToCheck);

			// Update availability status
			Object.entries(result).forEach(([username, available]) => {
				availabilityMap.value.set(
					username,
					available ? "available" : "taken",
				);
			});
		} catch (error) {
			console.error("Error checking username availability:", error);
			// On error, reset all to unchecked so users can retry
			usernamesToCheck.forEach((username) => {
				availabilityMap.value.delete(username);
			});
		}
	};

	/**
	 * Debounced check function (2 second delay)
	 */
	const debouncedCheck = useDebounceFn(performCheck, 2000);

	/**
	 * Watch for changes in usernames and trigger check
	 */
	watch(
		usernames,
		() => {
			debouncedCheck();
		},
		{ deep: true },
	);

	/**
	 * Gets the availability status for a specific username
	 */
	const getAvailabilityStatus = (
		username: string,
	): UsernameAvailabilityStatus => {
		if (!username || username.trim().length < 4) {
			return "unchecked";
		}

		const normalized = normalizeUsername(username);
		return availabilityMap.value.get(normalized) ?? "unchecked";
	};

	/**
	 * Checks if all provided usernames are available
	 */
	const allUsernamesAvailable = computed(() => {
		return usernames.value.every((username) => {
			if (!username || username.trim().length < 4) {
				return false;
			}
			const status = getAvailabilityStatus(username);
			return status === "available";
		});
	});

	return {
		getAvailabilityStatus,
		allUsernamesAvailable,
	};
}
